import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MonitorSmartphone, RefreshCw } from "lucide-react";
import { Spinner } from "@/components/common/FullScreenLoader";
import { EmptyState, SectionHeading } from "@/components/shell/StatCard";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { describeAgent, formatDateTime, shortId, timeAgo, timeUntil } from "@/lib/format";

export function SessionsPanel() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (e) {
      toast.error(apiError(e).message);
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function revoke(session) {
    setBusyId(session.id);
    try {
      const { data } = await api.delete(`/auth/sessions/${session.id}`);
      if (data.was_current) {
        await signOut();
        navigate("/sign-in", { replace: true });
        return;
      }
      toast.success("Session revoked");
      await load();
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function revokeOthers() {
    setBusyId("others");
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(data.count ? `${data.count} other session(s) signed out` : "No other sessions to revoke");
      await load();
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="bz-card">
      <SectionHeading
        title="Active sessions"
        description="Every device signed in to this account. Revocation is immediate and server-side."
        action={
          <div className="flex gap-2">
            <button type="button" onClick={load} className="bz-btn-ghost text-xs" data-testid="security-refresh-sessions-btn">
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} /> Refresh
            </button>
            <button type="button" onClick={revokeOthers} disabled={busyId === "others" || !sessions || sessions.length < 2} className="bz-btn-secondary text-xs" data-testid="security-revoke-others-btn">
              {busyId === "others" ? <Spinner className="h-3.5 w-3.5" /> : null} Sign out other sessions
            </button>
          </div>
        }
      />
      {sessions === null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
          <Spinner className="h-4 w-4 text-electric" /> Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <EmptyState testId="security-sessions-empty">No active sessions.</EmptyState>
      ) : (
        <div className="bz-table-wrap">
          <table data-testid="security-active-sessions-table" className="w-full">
            <thead>
              <tr>
                <th className="bz-th">Device</th>
                <th className="bz-th">IP</th>
                <th className="bz-th">Signed in</th>
                <th className="bz-th">Last seen</th>
                <th className="bz-th">Expires</th>
                <th className="bz-th text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="bz-tr" data-testid={`session-row-${s.id}`}>
                  <td className="bz-td">
                    <div className="flex items-center gap-2">
                      <MonitorSmartphone className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
                      <div>
                        <p className="text-white">{describeAgent(s.user_agent)}</p>
                        <p className="font-mono text-[11px] text-slate-500">
                          {shortId(s.id)}
                          {s.is_current ? <span className="ml-2 text-electric">· this device</span> : null}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="bz-td font-mono text-xs">{s.ip || "—"}</td>
                  <td className="bz-td text-slate-400">{formatDateTime(s.created_at)}</td>
                  <td className="bz-td text-slate-400">{timeAgo(s.last_seen_at)}</td>
                  <td className="bz-td font-mono text-xs text-slate-400">in {timeUntil(s.expires_at)}</td>
                  <td className="bz-td text-right">
                    <button type="button" onClick={() => revoke(s)} disabled={busyId === s.id} data-testid={`revoke-session-btn-${s.id}`} className={s.is_current ? "bz-btn-danger text-xs" : "bz-btn-secondary text-xs"}>
                      {busyId === s.id ? <Spinner className="h-3.5 w-3.5" /> : null}
                      {s.is_current ? "Sign out" : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
